import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'show', 'hide', 'message'];

  static values = {
    minLength: { type: Number, default: 8 },
    messageId: String,
    visible: Boolean
  };

  connect() {
    this.focused = false;
    this.syncHint();
  }

  // ── Public actions (wired from data-action) ────────────────────────────────
  toggle() {
    this.visibleValue = !this.visibleValue;
    // The clicked button is now hidden, so move focus to the one replacing it.
    this.activeToggle.focus();
  }

  focus() {
    this.focused = true;
    this.syncHint();
  }

  blur() {
    this.focused = false;
    this.syncHint();
  }

  sync() {
    this.syncHint();
  }

  // ── Visibility ────────────────────────────────────────────────────────────
  visibleValueChanged(visible) {
    if (!this.hasInputTarget) return;

    this.inputTarget.type = visible ? 'text' : 'password';
    if (!this.hasShowTarget) return;

    this.showTarget.hidden = visible;
    this.hideTarget.hidden = !visible;
  }

  get activeToggle() {
    return this.visibleValue ? this.hideTarget : this.showTarget;
  }

  // ── Hint ──────────────────────────────────────────────────────────────────
  // An error owns the message region while it is showing (React renders the
  // error instead of the hint), so leave it untouched until it is cleared.
  syncHint() {
    if (!this.hasMessageTarget || this.errorVisible) return;

    const show = this.focused && !this.meetsRequirements && this.hintText !== '';
    this.messageTarget.hidden = !show;
    this.describeByMessage(show);
  }

  get errorVisible() {
    return this.element.classList.contains('ui-input-field--error');
  }

  get hintText() {
    return this.element.dataset.inputFieldHint || '';
  }

  get meetsRequirements() {
    return this.inputTarget.value.length >= this.minLengthValue;
  }

  describeByMessage(present) {
    const ids = (this.inputTarget.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter(id => id && id !== this.messageIdValue);

    if (present) ids.unshift(this.messageIdValue);

    if (ids.length) {
      this.inputTarget.setAttribute('aria-describedby', ids.join(' '));
    } else {
      this.inputTarget.removeAttribute('aria-describedby');
    }
  }
}
